import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { PolicyMarkdown, processPolicyContent } from '../PolicyPage/policyMarkdown';
import { policyVersionLine } from '../PolicyPage/policySanitize';
import { acknowledgmentCheckedOnOpen, canPublish, type PublishGate } from './policyAdmin';

const Scroll = styled.div`
  ${tw`border border-border-default rounded-md px-4 py-3 overflow-y-auto bg-bg-secondary text-sm`}
  max-height: min(40vh, 320px);
`;

const Row = styled.label`
  ${tw`flex items-start gap-2 mt-4 text-sm cursor-pointer`}
`;

const Field = styled.label`
  ${tw`block text-sm mb-3`}
  span {
    ${tw`block mb-1 font-medium`}
  }
  input,
  textarea {
    ${tw`w-full border border-border-default rounded-md px-3 py-2`}
  }
`;

export type AcknowledgmentView = {
  id: string;
  versionNumber?: number | null;
  effectiveDate?: string | null;
  contentMarkdown?: string | null;
  approximateDates?: boolean | null;
};

/**
 * Publish is disabled until the acknowledgment box is ticked. The box is
 * controlled and starts unticked every time the dialog opens.
 */
export const PublishDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  acknowledgment: AcknowledgmentView | null;
  draftId: string | null;
  expectedCurrentVersionId: string | null;
  publishing: boolean;
  onPublish: (input: {
    draftId: string;
    expectedCurrentVersionId: string | null;
    changeSummary: string;
    effectiveDate: string;
    materialChange: boolean;
    acknowledgmentVersionId: string;
    platform: 'web';
  }) => void;
}> = ({
  open,
  onClose,
  acknowledgment,
  draftId,
  expectedCurrentVersionId,
  publishing,
  onPublish,
}) => {
  const [summary, setSummary] = useState('');
  const [date, setDate] = useState('');
  const [material, setMaterial] = useState(false);
  const [checked, setChecked] = useState<boolean>(acknowledgmentCheckedOnOpen);

  useEffect(() => {
    if (!open) return;
    setChecked(acknowledgmentCheckedOnOpen());
    setSummary('');
    setDate('');
    setMaterial(false);
  }, [open, acknowledgment?.id]);

  const gate: PublishGate = {
    draftId,
    changeSummary: summary,
    effectiveDate: date,
    acknowledgmentVersionId: acknowledgment?.id ?? null,
    acknowledgmentChecked: checked,
  };
  const line = policyVersionLine({
    effectiveDate: acknowledgment?.effectiveDate,
    versionNumber: acknowledgment?.versionNumber,
    approximateDates: acknowledgment?.approximateDates === true,
  });

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Publish this version"
      modalWidth="min(720px, calc(100vw - 2rem))"
    >
      <Field>
        <span>What changed</span>
        <textarea
          rows={3}
          value={summary}
          onChange={event => setSummary(event.target.value)}
          data-testid="publish-summary"
        />
      </Field>
      <Field>
        <span>Effective date</span>
        <input
          type="date"
          value={date}
          onChange={event => setDate(event.target.value)}
          data-testid="publish-date"
        />
      </Field>
      <Row>
        <input
          type="checkbox"
          checked={material}
          onChange={event => setMaterial(event.target.checked)}
          data-testid="publish-material"
        />
        <span>
          This is a material change. People who already accepted the previous version will be asked
          again.
        </span>
      </Row>
      <p tw="text-sm font-medium mt-4 mb-2">Legal Content Publishing Acknowledgment</p>
      {line && <p tw="text-xs text-text-secondary mb-2">{line}</p>}
      <Scroll data-testid="publish-acknowledgment">
        {acknowledgment?.contentMarkdown ? (
          <PolicyMarkdown content={processPolicyContent(acknowledgment.contentMarkdown)} />
        ) : (
          <p>The acknowledgment could not be loaded.</p>
        )}
      </Scroll>
      <Row>
        <input
          type="checkbox"
          checked={checked}
          onChange={event => setChecked(event.target.checked)}
          data-testid="publish-ack-checkbox"
        />
        <span>I have read this acknowledgment and I accept it for this publication.</span>
      </Row>
      <div tw="flex justify-end gap-2 mt-4">
        <Button text="Cancel" theme="secondary" type="button" onClick={onClose} />
        <Button
          text="Publish"
          type="button"
          isDisabled={!canPublish(gate) || publishing}
          isLoading={publishing}
          dataTestId="publish-confirm"
          onClick={() => {
            if (!draftId || !acknowledgment?.id || !canPublish(gate)) return;
            onPublish({
              draftId,
              expectedCurrentVersionId,
              changeSummary: summary.trim(),
              effectiveDate: date,
              materialChange: material,
              acknowledgmentVersionId: acknowledgment.id,
              platform: 'web',
            });
          }}
        />
      </div>
    </Modal>
  );
};
